import { useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, MinusSmallIcon, PlusSmallIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from '../services/api';
import toast from 'react-hot-toast';
import { TABLE_1_DOMAIN_SEQUENCE } from '../utils/masterTablesData';
import TreatmentAnalysisModal from '../components/TreatmentAnalysisModal';
import { formatInr } from '../utils/formatters';
import useAuthStore from '../store/useAuthStore';
import { normalizeRole, ROLE_KEYS } from '../utils/rolePermissions';

const RUPEE_SYMBOL = '\u20B9';
const PROFIT_MARGIN_MIN = -100;
const PROFIT_MARGIN_MAX = 500;
const PROFIT_MARGIN_STEP = 5;
const DEFAULT_PROFIT_MARGIN_PERCENT = 30;
const DURATION_MIN = 1;
const DURATION_STEP = 5;

const domains = TABLE_1_DOMAIN_SEQUENCE;

const stepperShellClasses =
  'inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 shadow-[0_2px_8px_rgba(15,23,42,0.04)]';
const stepperButtonClasses =
  'flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-900';
const headerCellClasses =
  'px-4 py-3.5 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500';
const bodyCellClasses = 'px-4 py-4 whitespace-nowrap text-center text-sm text-slate-600';

function toNumber(value, fallback = 0) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function clampValue(value, minValue, maxValue) {
  return Math.min(maxValue, Math.max(minValue, value));
}

function formatCurrency(value) {
  return formatInr(toNumber(value));
}

function isPricingReady(treatment = {}) {
  return treatment.pricingReady !== false;
}

function formatCalculatedCurrency(treatment, field) {
  if (!isPricingReady(treatment) || treatment[field] === null || treatment[field] === undefined) {
    return 'Not ready';
  }

  return formatCurrency(treatment[field]);
}

function calculateProfitMarginFromSellingPrice(breakEvenCost, sellingPrice) {
  const breakEvenValue = toNumber(breakEvenCost, 0);
  if (breakEvenValue <= 0) {
    return null;
  }

  return clampValue(
    ((toNumber(sellingPrice, 0) - breakEvenValue) / breakEvenValue) * 100,
    PROFIT_MARGIN_MIN,
    PROFIT_MARGIN_MAX
  );
}

function getSellingPriceToneClasses(treatment) {
  if (
    !isPricingReady(treatment) ||
    treatment.sellingPrice === null ||
    treatment.sellingPrice === undefined ||
    treatment.breakEvenCost === null ||
    treatment.breakEvenCost === undefined
  ) {
    return {
      shell: 'border-slate-200 bg-slate-50',
      text: 'text-slate-500',
    };
  }

  const sellingPrice = Math.round(toNumber(treatment.sellingPrice, 0) * 100);
  const breakEvenCost = Math.round(toNumber(treatment.breakEvenCost, 0) * 100);
  if (sellingPrice > breakEvenCost) {
    return {
      shell: 'border-emerald-200 bg-emerald-50 shadow-[0_6px_18px_rgba(5,150,105,0.10)]',
      text: 'text-emerald-800',
    };
  }

  if (sellingPrice < breakEvenCost) {
    return {
      shell: 'border-red-200 bg-red-50 shadow-[0_6px_18px_rgba(220,38,38,0.10)]',
      text: 'text-red-800',
    };
  }

  return {
    shell: 'border-blue-200 bg-blue-50 shadow-[0_6px_18px_rgba(37,99,235,0.10)]',
    text: 'text-blue-900',
  };
}

function buildDerivedTreatment(treatment = {}) {
  if (treatment.pricingReady === false) {
    return {
      ...treatment,
      duration: treatment.duration ?? 30,
      labCost: treatment.labCost ?? 0,
      consultantFees: treatment.consultantFees ?? 0,
      profitMargin: treatment.profitMargin ?? DEFAULT_PROFIT_MARGIN_PERCENT,
      fixedCost: null,
      variableCost: null,
      costPerMinute: null,
      breakEvenPerMin: null,
      breakEvenCost: null,
      sellingPrice: null,
    };
  }

  const durationInput = treatment.duration ?? 30;
  const labCostInput = treatment.labCost ?? 0;
  const consultantFeesInput = treatment.consultantFees ?? 0;
  const profitMarginInput = treatment.profitMargin ?? DEFAULT_PROFIT_MARGIN_PERCENT;
  const breakEvenPerMin = toNumber(treatment.costPerMinute ?? treatment.breakEvenPerMin, 0);
  const durationValue = toNumber(durationInput, 0);
  const variableCost = toNumber(treatment.variableCost, 0);
  const labCostValue = toNumber(labCostInput, 0);
  const consultantFeesValue = toNumber(consultantFeesInput, 0);
  const profitMarginValue = toNumber(profitMarginInput, 0);
  const fixedCost = breakEvenPerMin * durationValue;
  const breakEvenCost = fixedCost + variableCost + labCostValue + consultantFeesValue;
  const sellingPrice = breakEvenCost * (1 + profitMarginValue / 100);

  return {
    ...treatment,
    duration: durationInput,
    labCost: labCostInput,
    consultantFees: consultantFeesInput,
    profitMargin: profitMarginInput,
    variableCost,
    costPerMinute: breakEvenPerMin,
    breakEvenPerMin,
    fixedCost,
    breakEvenCost,
    sellingPrice,
  };
}

function normalizeFieldValue(field, value) {
  const numericValue = toNumber(value, 0);

  if (field === 'duration') {
    return Math.max(DURATION_MIN, numericValue);
  }

  if (field === 'labCost' || field === 'consultantFees') {
    return Math.max(0, numericValue);
  }

  if (field === 'profitMargin') {
    return clampValue(numericValue, PROFIT_MARGIN_MIN, PROFIT_MARGIN_MAX);
  }

  if (field === 'sellingPrice') {
    return Math.max(0, numericValue);
  }

  return value;
}

function MoneyInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  ariaLabel,
  disabled = false,
  shellClassName = '',
  inputClassName = 'text-slate-700',
}) {
  return (
    <div className={`inline-flex items-center rounded-full border px-3 py-1.5 shadow-[0_2px_8px_rgba(15,23,42,0.04)] ${shellClassName || 'border-slate-200 bg-slate-50'}`}>
      <span className="text-sm font-semibold text-slate-400">{RUPEE_SYMBOL}</span>
      <input
        type="number"
        min="0"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
        disabled={disabled}
        className={`w-20 border-0 bg-transparent px-2 text-center text-sm font-semibold outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-slate-400 ${inputClassName}`}
      />
    </div>
  );
}

function TreatmentEditorModal({ open, treatment, inventory, saving, onClose, onSave }) {
  const [draft, setDraft] = useState(null);
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (!open) return;
    const rows = treatment?.CustomTreatmentConsumableMappings || [];
    setDraft({
      treatmentName: treatment?.treatmentName || '',
      domainName: treatment?.domainName || domains[0],
      durationMinutes: treatment?.durationMinutes || 30,
      consumables: rows.filter((row) => row.isActive !== false && !row.deletedAt).map((row) => ({
        consumableId: row.consumableId, quantityUsed: row.quantityUsed,
      })),
    });
    setSearch('');
  }, [open, treatment]);
  if (!open || !draft) return null;
  const selected = new Map(draft.consumables.map((row) => [Number(row.consumableId), row]));
  const visible = inventory.filter((row) => `${row.name} ${row.typeVariant} ${row.domain}`.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-2 py-4 backdrop-blur-sm sm:items-center sm:px-4" role="dialog" aria-modal="true">
      <div className="max-h-[calc(100dvh-2rem)] w-full max-w-3xl overflow-y-auto rounded-[28px] border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4"><div><h2 className="text-lg font-semibold text-slate-900">{treatment ? 'Edit clinic treatment' : 'Add clinic treatment'}</h2><p className="text-sm text-slate-500">Configure duration and only the consumables this treatment uses.</p></div><button type="button" onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button></div>
        <form className="space-y-5 p-5" onSubmit={(e) => { e.preventDefault(); onSave(draft); }}>
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="sm:col-span-2"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Treatment Name</span><input required value={draft.treatmentName} onChange={(e) => setDraft({ ...draft, treatmentName: e.target.value })} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-300" /></label>
            <label><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Duration</span><div className="relative"><input required type="number" min="1" max="480" value={draft.durationMinutes} onChange={(e) => setDraft({ ...draft, durationMinutes: e.target.value })} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 pr-12 text-sm outline-none" /><span className="absolute bottom-3 right-4 text-xs text-slate-500">min</span></div></label>
            <label className="sm:col-span-3"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Category / Domain</span><select value={draft.domainName} onChange={(e) => setDraft({ ...draft, domainName: e.target.value })} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">{domains.map((domain) => <option key={domain}>{domain}</option>)}</select></label>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><div><h3 className="text-sm font-semibold text-slate-900">Consumables Used</h3><p className="text-xs text-slate-500">{draft.consumables.length} selected</p></div><input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search 400+ inventory items" className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm" /><div className="mt-3 max-h-64 space-y-2 overflow-y-auto">
            {visible.map((item) => { const mapping = selected.get(Number(item.id)); return <div key={item.id} className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:items-center"><label className="flex min-w-0 flex-1 items-center gap-2"><input type="checkbox" checked={Boolean(mapping)} onChange={() => setDraft((current) => ({ ...current, consumables: mapping ? current.consumables.filter((row) => Number(row.consumableId) !== Number(item.id)) : [...current.consumables, { consumableId: item.id, quantityUsed: 1 }] }))} /><span className="min-w-0"><span className="block truncate text-sm font-semibold text-slate-800">{item.name} · {item.typeVariant}</span><span className="block truncate text-xs text-slate-500">{item.domain}{item.isCustom ? ' · Clinic Added' : ''}</span></span></label>{mapping ? <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">Qty Used<input type="number" min="0" step="0.01" value={mapping.quantityUsed} onChange={(e) => setDraft((current) => ({ ...current, consumables: current.consumables.map((row) => Number(row.consumableId) === Number(item.id) ? { ...row, quantityUsed: e.target.value } : row) }))} className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-sm" /><span>{item.unit}</span></label> : null}</div>; })}
          </div></div>
          <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600">Cancel</button><button disabled={saving} className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Saving...' : treatment ? 'Save changes' : 'Add Treatment'}</button></div>
        </form>
      </div>
    </div>
  );
}

export default function PricingEngine() {
  const [selectedDomain, setSelectedDomain] = useState(domains[0]);
  const [treatments, setTreatments] = useState([]);
  const [loadingTreatments, setLoadingTreatments] = useState(false);
  const [treatmentLoadError, setTreatmentLoadError] = useState('');
  const [sellingPriceDrafts, setSellingPriceDrafts] = useState({});
  const [manualSellingPrices, setManualSellingPrices] = useState({});
  const fetchSequenceRef = useRef(0);
  const saveSequenceRef = useRef({});
  const rowSaveSequenceRef = useRef({});
  const manualSellingPricesRef = useRef({});
  const [analysisModal, setAnalysisModal] = useState({
    open: false,
    treatment: null,
    analysis: null,
    loading: false,
    error: '',
  });
  const analysisRequestRef = useRef(0);
  const analysisAbortRef = useRef(null);
  const analysisTriggerRef = useRef(null);
  const user = useAuthStore((state) => state.user);
  const canEditPricing = [ROLE_KEYS.OWNER_ADMIN, ROLE_KEYS.DOCTOR].includes(normalizeRole(user?.role));
  const [editor, setEditor] = useState({ open: false, treatment: null });
  const [inventory, setInventory] = useState([]);
  const [savingEditor, setSavingEditor] = useState(false);
  const [treatmentSearch, setTreatmentSearch] = useState('');
  const [treatmentCatalog, setTreatmentCatalog] = useState([]);
  const [treatmentSearchOpen, setTreatmentSearchOpen] = useState(false);
  const [pendingTreatmentFocus, setPendingTreatmentFocus] = useState(null);
  const [highlightedTreatmentId, setHighlightedTreatmentId] = useState(null);
  const treatmentRowRefs = useRef(new Map());

  const openTreatmentEditor = async (row = null) => {
    try {
      const [inventoryResponse, treatmentResponse] = await Promise.all([
        axios.get('/inventory/current'),
        row ? axios.get(`/pricing/custom/${row.customTreatmentId}`) : Promise.resolve({ data: null }),
      ]);
      setInventory(Array.isArray(inventoryResponse.data) ? inventoryResponse.data : []);
      setEditor({ open: true, treatment: treatmentResponse.data });
    } catch (error) { toast.error(error.response?.data?.error || 'Unable to open treatment editor'); }
  };

  const saveTreatmentEditor = async (draft) => {
    setSavingEditor(true);
    try {
      if (editor.treatment) await axios.put(`/pricing/custom-${editor.treatment.id}`, draft);
      else await axios.post('/pricing', draft);
      toast.success(editor.treatment ? 'Clinic treatment updated.' : 'Clinic treatment added.');
      setEditor({ open: false, treatment: null });
      if (draft.domainName !== selectedDomain) setSelectedDomain(draft.domainName); else await fetchTreatments();
      const catalogResponse = await axios.get('/pricing');
      setTreatmentCatalog(Array.isArray(catalogResponse.data) ? catalogResponse.data : []);
    } catch (error) { toast.error(error.response?.data?.error || 'Unable to save treatment'); }
    finally { setSavingEditor(false); }
  };

  useEffect(() => {
    fetchTreatments();
  }, [selectedDomain]);

  useEffect(() => {
    let active = true;
    axios.get('/pricing').then((response) => {
      if (active) setTreatmentCatalog(Array.isArray(response.data) ? response.data : []);
    }).catch(() => {
      if (active) setTreatmentCatalog([]);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!pendingTreatmentFocus || loadingTreatments || selectedDomain !== pendingTreatmentFocus.domain) return undefined;
    const row = treatmentRowRefs.current.get(String(pendingTreatmentFocus.id));
    if (!row) return undefined;
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    row.focus({ preventScroll: true });
    setHighlightedTreatmentId(String(pendingTreatmentFocus.id));
    setPendingTreatmentFocus(null);
    const timer = window.setTimeout(() => setHighlightedTreatmentId(null), 1800);
    return () => window.clearTimeout(timer);
  }, [treatments, loadingTreatments, pendingTreatmentFocus, selectedDomain]);

  const normalizedTreatmentSearch = treatmentSearch.trim().toLowerCase();
  const treatmentSearchResults = normalizedTreatmentSearch
    ? treatmentCatalog.filter((row) => String(row.name || '').toLowerCase().includes(normalizedTreatmentSearch)).slice(0, 12)
    : [];
  const chooseTreatmentSearchResult = (row) => {
    setTreatmentSearch(row.name);
    setTreatmentSearchOpen(false);
    setPendingTreatmentFocus({ id: row.id, domain: row.domain });
    if (selectedDomain !== row.domain) setSelectedDomain(row.domain);
  };
  const clearTreatmentSearch = () => {
    setTreatmentSearch('');
    setTreatmentSearchOpen(false);
    setPendingTreatmentFocus(null);
    setHighlightedTreatmentId(null);
  };

  const fetchTreatments = async () => {
    const requestId = fetchSequenceRef.current + 1;
    fetchSequenceRef.current = requestId;
    setLoadingTreatments(true);
    setTreatmentLoadError('');

    try {
      const res = await axios.get(`/pricing?domain=${encodeURIComponent(selectedDomain)}`);
      if (fetchSequenceRef.current !== requestId) {
        return;
      }

      setTreatments((res.data || []).map((treatment) => buildDerivedTreatment(treatment)));
      setSellingPriceDrafts({});
      setManualSellingPrices({});
      manualSellingPricesRef.current = {};
      const firstNotReady = (res.data || []).find((treatment) => treatment.pricingReady === false);
      if (firstNotReady) {
        setTreatmentLoadError(firstNotReady.pricingMessage || 'Setup incomplete - pricing calculation not ready.');
      }
    } catch (err) {
      console.error(err);
      if (fetchSequenceRef.current === requestId) {
        const message = err.response?.data?.error || 'Failed to load treatments';
        setTreatments([]);
        setTreatmentLoadError(message);
        toast.error(message);
      }
    } finally {
      if (fetchSequenceRef.current === requestId) {
        setLoadingTreatments(false);
      }
    }
  };

  const persistTreatmentUpdate = async (id, field, value, extraPayload = {}, options = {}) => {
    const payload = { [field]: value, ...extraPayload };
    const requestKey = `${id}:${Object.keys(payload).sort().join(',')}`;
    const requestId = (saveSequenceRef.current[requestKey] || 0) + 1;
    const rowRequestId = (rowSaveSequenceRef.current[id] || 0) + 1;
    saveSequenceRef.current[requestKey] = requestId;
    rowSaveSequenceRef.current[id] = rowRequestId;

    try {
      const res = await axios.put(`/pricing/${id}`, payload);

      if (
        saveSequenceRef.current[requestKey] !== requestId ||
        rowSaveSequenceRef.current[id] !== rowRequestId
      ) {
        return;
      }

      const updatedRow = buildDerivedTreatment(res.data || {});
      if (options.expectedProfitMargin !== undefined) {
        const returnedMargin = toNumber(updatedRow.profitMargin, NaN);
        if (
          !Number.isFinite(returnedMargin) ||
          Math.abs(returnedMargin - options.expectedProfitMargin) > 0.05
        ) {
          toast.error(
            'Selling price changed locally, but the server did not save the negative margin. Please restart the backend and try again.'
          );
          return;
        }
      }

      setTreatments((current) =>
        current.map((treatment) => {
          if (treatment.id !== id) {
            return treatment;
          }

          return buildDerivedTreatment({
            ...treatment,
            costPerMinute: updatedRow.costPerMinute,
            breakEvenPerMin: updatedRow.breakEvenPerMin,
            variableCost: updatedRow.variableCost,
            duration: updatedRow.duration ?? treatment.duration,
            labCost: updatedRow.labCost ?? treatment.labCost,
            consultantFees: updatedRow.consultantFees ?? treatment.consultantFees,
            profitMargin: updatedRow.profitMargin ?? treatment.profitMargin,
          });
        })
      );
    } catch (err) {
      if (
        saveSequenceRef.current[requestKey] !== requestId ||
        rowSaveSequenceRef.current[id] !== rowRequestId
      ) {
        return;
      }

      console.error(err);
      toast.error(err.response?.data?.error || 'Update failed');
      if (!options.keepLocalOnError) {
        fetchTreatments();
      }
    }
  };

  const closeAnalysis = useCallback(() => {
    analysisRequestRef.current += 1;
    analysisAbortRef.current?.abort();
    analysisAbortRef.current = null;
    setAnalysisModal({ open: false, treatment: null, analysis: null, loading: false, error: '' });
  }, []);

  const openAnalysis = async (treatment, triggerElement) => {
    analysisRequestRef.current += 1;
    const requestId = analysisRequestRef.current;
    analysisAbortRef.current?.abort();
    const controller = new AbortController();
    analysisAbortRef.current = controller;
    analysisTriggerRef.current = triggerElement;
    setAnalysisModal({
      open: true,
      treatment,
      analysis: null,
      loading: true,
      error: '',
    });
    try {
      const response = await axios.get(`/pricing/${encodeURIComponent(treatment.id)}/analysis`, {
        signal: controller.signal,
      });
      if (analysisRequestRef.current !== requestId || controller.signal.aborted) return;
      setAnalysisModal((current) => ({
        ...current,
        analysis: response.data,
        loading: false,
        error: '',
      }));
    } catch (error) {
      if (controller.signal.aborted || analysisRequestRef.current !== requestId) return;
      setAnalysisModal((current) => ({
        ...current,
        analysis: null,
        loading: false,
        error: error.response?.data?.error || error.message || 'Unable to load treatment analysis',
      }));
    }
  };

  const handleFieldChange = (id, field, value) => {
    const normalizedValue = normalizeFieldValue(field, value);

    if (field === 'sellingPrice') {
      handleSellingPriceDraftChange(id, value);
      return;
    }

    const currentTreatment = treatments.find((treatment) => treatment.id === id);
    const manualSellingPrice =
      manualSellingPricesRef.current[id] ??
      manualSellingPrices[id] ??
      (
        sellingPriceDrafts[id] !== undefined && sellingPriceDrafts[id] !== ''
          ? normalizeFieldValue('sellingPrice', sellingPriceDrafts[id])
          : undefined
      );
    const shouldPreserveManualSellingPrice =
      ['duration', 'labCost', 'consultantFees'].includes(field) &&
      manualSellingPrice !== undefined &&
      currentTreatment &&
      isPricingReady(currentTreatment);
    let nextProfitMarginForManualPrice = null;

    if (shouldPreserveManualSellingPrice) {
      const nextBreakEvenRow = buildDerivedTreatment({
        ...currentTreatment,
        [field]: normalizedValue,
        profitMargin: 0,
      });
      nextProfitMarginForManualPrice = calculateProfitMarginFromSellingPrice(
        nextBreakEvenRow.breakEvenCost,
        manualSellingPrice
      );
    }

    setTreatments((current) =>
      current.map((treatment) =>
        treatment.id === id
          ? buildDerivedTreatment({
              ...treatment,
              [field]: normalizedValue,
              ...(nextProfitMarginForManualPrice !== null
                ? { profitMargin: nextProfitMarginForManualPrice }
                : {}),
            })
          : treatment
      )
    );

    persistTreatmentUpdate(
      id,
      field,
      normalizedValue,
      nextProfitMarginForManualPrice !== null
        ? { profitMargin: nextProfitMarginForManualPrice }
        : {},
      nextProfitMarginForManualPrice !== null
        ? {
            expectedProfitMargin: nextProfitMarginForManualPrice,
            keepLocalOnError: true,
          }
        : {}
    );
  };

  const handleSellingPriceDraftChange = (id, rawValue) => {
    setSellingPriceDrafts((current) => ({ ...current, [id]: rawValue }));

    if (rawValue === '') {
      return;
    }

    const normalizedValue = normalizeFieldValue('sellingPrice', rawValue);
    const currentTreatment = treatments.find((treatment) => treatment.id === id);

    if (!currentTreatment || !isPricingReady(currentTreatment) || toNumber(currentTreatment.breakEvenCost, 0) <= 0) {
      toast.error('Selling price cannot be updated until break-even cost is ready.');
      return;
    }

    const nextProfitMargin = calculateProfitMarginFromSellingPrice(
      currentTreatment.breakEvenCost,
      normalizedValue
    );

    if (nextProfitMargin === null) {
      toast.error('Selling price cannot be updated until break-even cost is ready.');
      return;
    }

    setTreatments((current) =>
      current.map((treatment) =>
        treatment.id === id
          ? buildDerivedTreatment({ ...treatment, profitMargin: nextProfitMargin })
          : treatment
      )
    );
  };

  const commitSellingPriceChange = (id) => {
    const currentTreatment = treatments.find((treatment) => treatment.id === id);
    const rawValue = sellingPriceDrafts[id];

    if (rawValue === undefined) {
      return;
    }

    if (rawValue === '') {
      setSellingPriceDrafts((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      return;
    }

    if (!currentTreatment || !isPricingReady(currentTreatment) || toNumber(currentTreatment.breakEvenCost, 0) <= 0) {
      toast.error('Selling price cannot be updated until break-even cost is ready.');
      return;
    }

    const normalizedValue = normalizeFieldValue('sellingPrice', rawValue);
    const nextProfitMargin = calculateProfitMarginFromSellingPrice(
      currentTreatment.breakEvenCost,
      normalizedValue
    );

    if (nextProfitMargin === null) {
      toast.error('Selling price cannot be updated until break-even cost is ready.');
      return;
    }

    manualSellingPricesRef.current = {
      ...manualSellingPricesRef.current,
      [id]: normalizedValue,
    };
    setManualSellingPrices((current) => ({ ...current, [id]: normalizedValue }));
    setSellingPriceDrafts((current) => ({ ...current, [id]: String(normalizedValue) }));
    setTreatments((current) =>
      current.map((treatment) =>
        treatment.id === id
          ? buildDerivedTreatment({ ...treatment, profitMargin: nextProfitMargin })
          : treatment
      )
    );
    persistTreatmentUpdate(
      id,
      'sellingPrice',
      normalizedValue,
      { profitMargin: nextProfitMargin },
      {
        expectedProfitMargin: nextProfitMargin,
        keepLocalOnError: true,
      }
    );
  };

  const getSellingPriceInputValue = (treatment) => {
    if (sellingPriceDrafts[treatment.id] !== undefined) {
      return sellingPriceDrafts[treatment.id];
    }

    return toNumber(treatment.sellingPrice, 0).toFixed(2);
  };

  const adjustProfitMargin = (id, currentValue, delta) => {
    const nextValue = clampValue(
      toNumber(currentValue, 0) + delta,
      PROFIT_MARGIN_MIN,
      PROFIT_MARGIN_MAX
    );

    handleFieldChange(id, 'profitMargin', nextValue);
    const nextManualSellingPrices = { ...manualSellingPricesRef.current };
    delete nextManualSellingPrices[id];
    manualSellingPricesRef.current = nextManualSellingPrices;
    setManualSellingPrices((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
    setSellingPriceDrafts((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  };

  const adjustDuration = (id, currentValue, delta) => {
    const nextValue = Math.max(DURATION_MIN, toNumber(currentValue, DURATION_MIN) + delta);
    handleFieldChange(id, 'duration', nextValue);
  };

  return (
    <div className="space-y-3">
      <section className="rounded-[24px] border border-slate-200 bg-white px-5 py-4 shadow-[0_8px_22px_rgba(15,23,42,0.04)]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-tight tracking-tight text-slate-900">
              Pricing Engine
            </h1>
            <p className="mt-1.5 max-w-2xl text-sm leading-5 text-slate-500">
              Review treatment costs, set duration and fees, and finalize your clinic&apos;s selling price.
            </p>
          </div>
          {canEditPricing ? (
            <button
              type="button"
              onClick={() => openTreatmentEditor()}
              className="inline-flex w-full shrink-0 items-center justify-center rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_18px_rgba(37,99,235,0.20)] transition hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-100 sm:w-auto"
            >
              <PlusSmallIcon className="mr-1 h-5 w-5" />
              Add Treatment
            </button>
          ) : null}
        </div>
      </section>

      <div className="flex flex-col gap-2 rounded-[20px] border border-slate-200 bg-slate-50/80 p-2 shadow-[0_6px_18px_rgba(15,23,42,0.04)] md:flex-row md:items-center">
      <div className="relative w-full shrink-0 md:w-80">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-blue-500" />
        <input type="search" value={treatmentSearch} onFocus={() => setTreatmentSearchOpen(true)} onChange={(event) => { setTreatmentSearch(event.target.value); setTreatmentSearchOpen(true); }} onKeyDown={(event) => { if (event.key === 'Escape') setTreatmentSearchOpen(false); if (event.key === 'Enter' && treatmentSearchResults[0]) { event.preventDefault(); chooseTreatmentSearchResult(treatmentSearchResults[0]); } }} placeholder="Search treatments..." aria-label="Search treatments" className="h-11 w-full rounded-2xl border border-blue-200 bg-blue-50/40 pl-12 pr-11 text-sm text-slate-800 outline-none transition placeholder:text-slate-500 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" />
        {treatmentSearch ? <button type="button" onClick={clearTreatmentSearch} aria-label="Clear treatment search" className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><XMarkIcon className="h-4 w-4" /></button> : null}
        {treatmentSearchOpen && normalizedTreatmentSearch ? <div className="absolute z-40 mt-2 max-h-80 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">{treatmentSearchResults.length ? treatmentSearchResults.map((row) => <button key={row.id} type="button" onClick={() => chooseTreatmentSearchResult(row)} className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-blue-50"><span className="min-w-0"><span className="block truncate text-sm font-semibold text-slate-900">{row.name}</span><span className="block truncate text-xs text-slate-500">{row.domain}</span></span>{row.isCustom ? <span className="shrink-0 rounded-full bg-teal-50 px-2 py-1 text-[10px] font-semibold uppercase text-teal-700">Clinic Added</span> : null}</button>) : <p className="px-3 py-4 text-sm text-slate-500">No treatments found</p>}</div> : null}
      </div>

      <div className="mobile-table-scroll min-w-0 flex-1">
        <div className="flex min-w-max gap-2">
          {domains.map((domain) => {
            const isActive = selectedDomain === domain;

            return (
              <button
                key={domain}
                onClick={() => setSelectedDomain(domain)}
                className={`rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-[0_8px_20px_rgba(37,99,235,0.22)]'
                    : 'border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
                }`}
              >
                {domain}
              </button>
            );
          })}
        </div>
      </div>
      </div>

      <div className="mobile-table-scroll rounded-[28px] border border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.05)]">
        {treatmentLoadError && treatments.length > 0 ? (
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-3 text-sm font-semibold text-amber-800">
            {treatmentLoadError}
          </div>
        ) : null}
        <table className="mobile-card-table min-w-[1220px] divide-y divide-slate-200">
          <thead className="sticky top-0 z-20 bg-slate-50/95 shadow-[0_1px_0_rgba(148,163,184,0.35)]">
            <tr>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Treatment
              </th>
              <th className={headerCellClasses}>Duration</th>
              <th className={headerCellClasses}>Fixed Cost</th>
              <th className={headerCellClasses}>Variable Cost</th>
              <th className={headerCellClasses}>{`Lab Cost (${RUPEE_SYMBOL})`}</th>
              <th className={headerCellClasses}>{`Consultant Fees (${RUPEE_SYMBOL})`}</th>
              <th className={headerCellClasses}>Break-even Cost</th>
              <th className={headerCellClasses}>Profit Margin %</th>
              <th className={headerCellClasses}>Selling Price</th>
              <th className={headerCellClasses}>Analysis</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 bg-white">
            {treatments.map((treatment) => (
              <tr key={treatment.id} ref={(node) => { if (node) treatmentRowRefs.current.set(String(treatment.id), node); else treatmentRowRefs.current.delete(String(treatment.id)); }} tabIndex="-1" className={`outline-none transition duration-500 ${String(highlightedTreatmentId) === String(treatment.id) ? 'bg-blue-50 ring-2 ring-inset ring-blue-200' : 'hover:bg-slate-50/50'}`}>
                <td data-label="Treatment" className="px-5 py-4 text-left">
                  <div className="min-w-[250px]">
                    <p className="text-sm font-semibold text-slate-900">{treatment.name}</p>
                    {treatment.isCustom ? <span className="mt-1 inline-flex rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-700">Clinic Added</span> : null}
                    <p className="mt-1 text-xs font-medium uppercase tracking-[0.16em] text-slate-400">
                      {selectedDomain}
                    </p>
                  </div>
                </td>

                <td data-label="Duration" className={bodyCellClasses}>
                  <div className={stepperShellClasses}>
                    <button
                      type="button"
                      onClick={() => adjustDuration(treatment.id, treatment.duration, -DURATION_STEP)}
                      className={stepperButtonClasses}
                      aria-label={`Decrease duration for ${treatment.name}`}
                    >
                      <MinusSmallIcon className="h-4 w-4" />
                    </button>

                    <input
                      type="number"
                      min={DURATION_MIN}
                      value={treatment.duration}
                      onChange={(event) => handleFieldChange(treatment.id, 'duration', event.target.value)}
                      className="w-14 border-0 bg-transparent p-0 text-center text-sm font-semibold text-slate-700 outline-none focus:ring-0"
                      aria-label={`Duration for ${treatment.name}`}
                    />

                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">min</span>

                    <button
                      type="button"
                      onClick={() => adjustDuration(treatment.id, treatment.duration, DURATION_STEP)}
                      className={stepperButtonClasses}
                      aria-label={`Increase duration for ${treatment.name}`}
                    >
                      <PlusSmallIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>

                <td data-label="Fixed Cost" className={`${bodyCellClasses} font-semibold tabular-nums text-slate-800`}>
                  {formatCalculatedCurrency(treatment, 'fixedCost')}
                </td>

                <td data-label="Variable Cost" className={`${bodyCellClasses} font-semibold tabular-nums text-slate-800`}>
                  {formatCalculatedCurrency(treatment, 'variableCost')}
                </td>

                <td data-label="Lab Cost" className={bodyCellClasses}>
                  <MoneyInput
                    value={treatment.labCost}
                    onChange={(event) => handleFieldChange(treatment.id, 'labCost', event.target.value)}
                    ariaLabel={`Lab cost for ${treatment.name}`}
                  />
                </td>

                <td data-label="Consultant Fees" className={bodyCellClasses}>
                  <MoneyInput
                    value={treatment.consultantFees}
                    onChange={(event) => handleFieldChange(treatment.id, 'consultantFees', event.target.value)}
                    ariaLabel={`Consultant fees for ${treatment.name}`}
                  />
                </td>

                <td data-label="Break-even" className={`${bodyCellClasses} font-semibold tabular-nums text-slate-900`}>
                  {formatCalculatedCurrency(treatment, 'breakEvenCost')}
                </td>

                <td data-label="Profit Margin" className={bodyCellClasses}>
                  <div className={stepperShellClasses}>
                    <button
                      type="button"
                      onClick={() => adjustProfitMargin(treatment.id, treatment.profitMargin, -PROFIT_MARGIN_STEP)}
                      className={stepperButtonClasses}
                      aria-label={`Decrease profit margin for ${treatment.name}`}
                    >
                      <MinusSmallIcon className="h-4 w-4" />
                    </button>

                    <input
                      type="number"
                      min={PROFIT_MARGIN_MIN}
                      max={PROFIT_MARGIN_MAX}
                      step="1"
                      value={treatment.profitMargin}
                      onChange={(event) => handleFieldChange(treatment.id, 'profitMargin', event.target.value)}
                      className="w-14 border-0 bg-transparent p-0 text-center text-sm font-semibold text-slate-700 outline-none focus:ring-0"
                      aria-label={`Profit margin for ${treatment.name}`}
                    />

                    <span className="text-xs font-semibold text-slate-400">%</span>

                    <button
                      type="button"
                      onClick={() => adjustProfitMargin(treatment.id, treatment.profitMargin, PROFIT_MARGIN_STEP)}
                      className={stepperButtonClasses}
                      aria-label={`Increase profit margin for ${treatment.name}`}
                    >
                      <PlusSmallIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>

                <td data-label="Selling Price" className={bodyCellClasses}>
                  {isPricingReady(treatment) && treatment.sellingPrice !== null && treatment.sellingPrice !== undefined ? (
                    <MoneyInput
                      value={getSellingPriceInputValue(treatment)}
                      onChange={(event) => handleFieldChange(treatment.id, 'sellingPrice', event.target.value)}
                      onBlur={() => commitSellingPriceChange(treatment.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.currentTarget.blur();
                        }
                      }}
                      ariaLabel={`Selling price for ${treatment.name}`}
                      shellClassName={getSellingPriceToneClasses(treatment).shell}
                      inputClassName={`text-base font-bold tracking-tight tabular-nums ${getSellingPriceToneClasses(treatment).text}`}
                    />
                  ) : (
                    <div className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-slate-50 px-4 py-2">
                      <span className="text-sm font-semibold text-slate-500">
                        {formatCalculatedCurrency(treatment, 'sellingPrice')}
                      </span>
                    </div>
                  )}
                </td>
                <td data-label="Analysis" className={bodyCellClasses}>
                  <button
                    type="button"
                    onClick={(event) => openAnalysis(treatment, event.currentTarget)}
                    className="rounded-full border border-teal-200 bg-[#ecfdf7] px-4 py-2 text-xs font-semibold text-[#0d9488] transition hover:border-teal-300 hover:bg-teal-100"
                  >
                    View Analysis
                  </button>
                  {treatment.isCustom && canEditPricing ? <button type="button" onClick={() => openTreatmentEditor(treatment)} className="ml-2 rounded-full border border-teal-200 bg-teal-50 px-4 py-2 text-xs font-semibold text-teal-700 hover:bg-teal-100">Edit</button> : null}
                </td>
              </tr>
            ))}

            {treatments.length === 0 && (
              <tr>
                <td colSpan="10" data-full-row="true" className="px-6 py-8 text-center text-sm text-slate-500">
                  {loadingTreatments
                    ? 'Loading treatments...'
                    : treatmentLoadError || 'No treatments found for this domain.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <TreatmentAnalysisModal
        open={analysisModal.open}
        treatment={analysisModal.treatment}
        analysis={analysisModal.analysis}
        loading={analysisModal.loading}
        error={analysisModal.error}
        onClose={closeAnalysis}
        returnFocusRef={analysisTriggerRef}
      />
      <TreatmentEditorModal open={editor.open} treatment={editor.treatment} inventory={inventory} saving={savingEditor} onClose={() => setEditor({ open: false, treatment: null })} onSave={saveTreatmentEditor} />
    </div>
  );
}
